import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  getRatingSummaries,
  getCollectorsSummaries,
  type CollectorAvatar,
} from "@/lib/ratings.functions";

export type AlbumCommunityMeta = {
  score: number | null;
  raters: number;
  collectorsCount: number;
  collectors: CollectorAvatar[];
};

const EMPTY: AlbumCommunityMeta = { score: null, raters: 0, collectorsCount: 0, collectors: [] };

/**
 * Notas da comunidade + quem tem cada disco, para uma lista de cards.
 * Retorna um getter estável por discogs_id.
 */
export function useAlbumCommunityMeta(discogsIds: (string | null | undefined)[]) {
  const ids = [...new Set(discogsIds.filter(Boolean) as string[])].slice(0, 60).sort();
  const ratingsFn = useServerFn(getRatingSummaries);
  const collectorsFn = useServerFn(getCollectorsSummaries);

  const ratings = useQuery({
    queryKey: ["rating-summaries", ids],
    queryFn: () => ratingsFn({ data: { discogsIds: ids } }),
    enabled: ids.length > 0,
    staleTime: 5 * 60_000,
  });

  const collectors = useQuery({
    queryKey: ["collectors-summaries", ids],
    queryFn: () => collectorsFn({ data: { discogsIds: ids } }),
    enabled: ids.length > 0,
    staleTime: 5 * 60_000,
  });

  const map = new Map<string, AlbumCommunityMeta>();
  for (const r of ratings.data ?? []) {
    map.set(r.discogsId, { ...EMPTY, score: r.score, raters: r.raters });
  }
  for (const c of collectors.data ?? []) {
    const prev = map.get(c.discogsId) ?? EMPTY;
    map.set(c.discogsId, { ...prev, collectorsCount: c.count, collectors: c.people });
  }

  return (discogsId: string | null | undefined): AlbumCommunityMeta =>
    (discogsId ? map.get(discogsId) : undefined) ?? EMPTY;
}
